import React from "react";
import {
  ApiOutlined,
  CalendarOutlined,
  CloudUploadOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  SafetyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";

type StudioFeature = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const studioFeatures: StudioFeature[] = [
  {
    title: "YouTube channel management",
    description:
      "Connect authorized YouTube channels and manage them from a single Studio interface.",
    icon: <YoutubeIcon />,
  },
  {
    title: "Video uploading",
    description:
      "Upload video files to the authorized channel with titles, descriptions and metadata.",
    icon: <CloudUploadOutlined />,
  },
  {
    title: "Video scheduling",
    description:
      "Schedule videos and classes to publish at a chosen date and time.",
    icon: <CalendarOutlined />,
  },
  {
    title: "Video management",
    description:
      "Review uploaded videos, update details and track publishing status and history.",
    icon: <PlayCircleOutlined />,
  },
  {
    title: "Live-stream creation & configuration",
    description:
      "Create live broadcasts, configure stream keys and bind them to the authorized channel.",
    icon: <ApiOutlined />,
  },
  {
    title: "Start & end live broadcasts",
    description:
      "Take broadcasts live and end them when a session finishes, directly from KGS Studio.",
    icon: <VideoCameraOutlined />,
  },
  {
    title: "Live-stream monitoring",
    description:
      "Monitor live, rendering, queued, halted and failed jobs on the Studio dashboard.",
    icon: <DesktopOutlined />,
  },
  {
    title: "Authorized access only",
    description:
      "Studio tools are available to authenticated users after explicit YouTube authorization.",
    icon: <SafetyOutlined />,
  },
];

function YoutubeIcon() {
  return (
    <span className="text-[18px]">
      <PlayCircleOutlined />
    </span>
  );
}

const AboutStudio = () => {
  return (
    <section
      id="about"
      className="bg-[#F7F8FC] px-5 py-16 sm:px-8 sm:py-20 lg:px-10 lg:py-24"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="max-w-3xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#00A862]">
              About KGS Studio
            </span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-[#1D1F5D] sm:text-4xl">
            What KGS Studio is
          </h2>

          <p className="mt-2 text-base leading-7 text-[#64748B] sm:text-[17px]">
            KGS Studio is a video and live-stream management platform
            developed and operated by{" "}
            <strong className="font-semibold text-[#172033]">
              Khan Global Studies
            </strong>
            . It is a product of Khan
            Global Studies, used by its authorized team members and creators to
            publish educational classes and manage live broadcasts on authorized
            YouTube channels.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {studioFeatures.map((feature) => (
            <article
              key={feature.title}
              className="group rounded-xl border border-[#E2E5ED] bg-white p-5 shadow-[0_4px_14px_rgba(29,31,93,0.04)] transition-all duration-200 hover:-translate-y-1 hover:border-[#00B86B]/30 hover:shadow-[0_10px_25px_rgba(29,31,93,0.08)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E8F8F0] text-[#1D1F5D] transition-colors duration-200 group-hover:bg-[#00B86B] group-hover:text-white">
                {feature.icon}
              </div>
              <h3 className="mt-2 text-[15px] font-bold leading-5 text-[#172033]">
                {feature.title}
              </h3>
              <p className="mt-2.5 text-sm leading-5 text-[#64748B]">
                {feature.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutStudio;
